import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { OnboardingStatus } from "../../model/entities/onboarding-status";
import { Profil } from "../../model/entities/profil";
import { AuthenticationState } from "../../model/state/authentication-state";
import { ApplicationState } from "../../store";
import { isScaModeActive } from "../../helpers/convert-env-variables-helper";
import { ProfilCode, UserInfoResponse } from "../../model/dto/response";
import { StatutSca } from "../../model/entities/statut-sca";

type Props = {
  status?: OnboardingStatus;
  kycErrorCount?: number
  userInfo?: UserInfoResponse
};

export function ProfilCompletionHeader({ status, kycErrorCount, userInfo }: Props) {

  const loginProps: AuthenticationState = useSelector<ApplicationState, AuthenticationState>((state) => state.authentication);

  const isDon = useMemo(()=>{
    if(loginProps.user?.Etablissement) {
      return true
    }
    else {
      return false
    }
  }, []) 

  const KycErrorMessageWithoutScaMode =  "Vous pouvez toujours investir dans les projets présentés sur la plateforme. Toutefois, il faut recommencer la validation de votre pièce d’identité."
  const KycErrorMessageWithScaMode = "Une erreur a été détectée dans une ou plusieurs de vos pièces justificatives, vous pouvez avoir plus d’informations et nous transmettre un nouveau document via la section dédiée ci-dessous."
  const KycMaxErrorCountMessage = 'Suite à de trop nombreux échecs dans le traitement de vos documents nous vous invitons à prendre contact avec le support Villyz à l\'adresse contact@villyz.fr afin de valider votre profil.'

  const displayErrorDescriptionMessageForInvestisseur = () => {
    let KYCerrorMessage: string =""
    let SCAerrorMessage: string= ""
    if(isScaModeActive && userInfo?.ProfilCode === Profil.INVESTISSEUR && userInfo.Etablissement === null){
        if(userInfo.KYCValidated !== true ){
          if(kycErrorCount && kycErrorCount >= 3){
            KYCerrorMessage = KycMaxErrorCountMessage
          }
          else{
            KYCerrorMessage = KycErrorMessageWithScaMode
          }
        }
        if(userInfo?.StatutScaCode === StatutSca.EN_ERREUR){
          SCAerrorMessage = "La validation de votre numéro de téléphone a rencontré un petit soucis. Vous devez refaire sa validation dans la section “Sécurité du compte” ci-dessous."
        }
        return `${KYCerrorMessage} ${SCAerrorMessage}`
      }
    else {
      if(userInfo?.KYCValidated === false ){
        if(kycErrorCount && kycErrorCount >= 3){
          KYCerrorMessage = KycMaxErrorCountMessage
        }
        else{
          KYCerrorMessage = isScaModeActive ? KycErrorMessageWithScaMode : KycErrorMessageWithoutScaMode
        }
      return KYCerrorMessage
      }
    }
  }

  const config = [
    {
      code: OnboardingStatus.PARTIELLEMENT_COMPLET,
      profile: Profil.PORTEUR_PROJET,
      description:
        "Vous pouvez commencer à créer vos demandes de financement. Vous devrez soumettre au moins l’identité et l’adresse du représentant, ainsi que l’adresse de l'établissement avant la publications de vos demandes de financement.",
      bgColor: "#fcf8e3",
      textColor: "#8a6d3b",
    },
    {
      code: OnboardingStatus.PRESQUE_COMPLET,
      profile: Profil.PORTEUR_PROJET,
      description:
        "Vous pouvez d’ores et déjà publier vos demandes de financement à Villyz. Vous devrez cependant soumettre vos pièces justificatives avant de pouvoir déboucler les fonds collectés.",
      bgColor: "#fcf8e3",
      textColor: "#8a6d3b",
    },
    {
      code: OnboardingStatus.COMPLET,
      profile: Profil.PORTEUR_PROJET,
      description:
        "Votre profil est complet, il va être validé par nos équipes sous 48h. Vos demandes de financement pourront ensuite être publiées.",
      bgColor: "#d9edf7",
      textColor: "#31708f",
    },
    {
      code: OnboardingStatus.VALIDE,
      profile: Profil.PORTEUR_PROJET,
      description:
        "Votre profil est complet et validé par nos équipes. Vous avez accès à toutes les fonctionnalités de Villyz pour un compte professionnel.",
      bgColor: "#dff0d8",
      textColor: "#3c763d",
    },
    {
      code: OnboardingStatus.ERREUR,
      profile: Profil.PORTEUR_PROJET,
      description:
        "Vous pouvez toujours publier vos projets sur la plateforme. Toutefois, il faut recommencer la validation de votre pièce d’identité.",
      bgColor: "#f2dede",
      textColor: "#a94442",
    },
    {
      code: OnboardingStatus.PARTIELLEMENT_COMPLET,
      profile: Profil.INVESTISSEUR,
      description: !isDon ? "Renseignez votre profil en quelques clics pour commencer à investir." : "Complétez votre profil en quelques clics pour commencer à effectuer des dons.",
      bgColor: "#fcf8e3",
      textColor: "#8a6d3b",
    },
    {
      code: OnboardingStatus.PRESQUE_COMPLET,
      profile: Profil.INVESTISSEUR,
      description:
        !isDon ? "Vous pouvez investir, n'oubliez pas cependant de finaliser la validation de votre compte afin de pouvoir effectuer vos futurs retraits de fonds."
        : "Vous pouvez maintenant commencer à effectuer des dons.",
      bgColor: "#d9edf7",
      textColor: "#31708f",
    },
    {
      code: OnboardingStatus.COMPLET,
      profile: Profil.INVESTISSEUR,
      description: !isDon ? "Vous pouvez commencer à investir." : "Votre profil est complet. Vous pouvez maintenant commencer à effectuer des dons.",
      bgColor: "#d9edf7",
      textColor: "#31708f",
    },
    {
      code: OnboardingStatus.VALIDE,
      profile: Profil.INVESTISSEUR,
      description:
        "Votre profil est complet et validé par nos équipes. Vous avez accès à toutes les fonctionnalités de Villyz pour un compte particulier.",
      bgColor: "#dff0d8",
      textColor: "#3c763d",
    },
    {
      code: OnboardingStatus.ERREUR,
      profile: Profil.INVESTISSEUR,
      description:
      displayErrorDescriptionMessageForInvestisseur(),
      bgColor: "#f2dede",
      textColor: "#a94442",
    },
    {
      code: OnboardingStatus.VAL_EMAIL_A_FAIRE,
      profile: Profil.INVESTISSEUR,
      description: "Vos pièces justificatives sont validées. Merci de maintenant valider votre adresse mail afin de pouvoir accéder à toutes les fonctionnalités de la plateforme.",
      bgColor: "#fcf8e3",
      textColor: "#8a6d3b",
    },
    {
      code: OnboardingStatus.VAL_EMAIL_A_FAIRE,
      profile: Profil.PORTEUR_PROJET,
      description: "Vos pièces justificatives sont validées. Merci de maintenant valider votre adresse mail afin de pouvoir accéder à toutes les fonctionnalités de la plateforme.",
      bgColor: "#fcf8e3",
      textColor: "#8a6d3b",
    },
  ];

  const activeConfig = useMemo(() => {
    var mappedConfig = config.find(
      (c) =>
        c.code === status?.Code && c.profile === loginProps.oauth?.profilCode
    );

    if(status?.Code === OnboardingStatus.ERREUR && kycErrorCount && kycErrorCount >= 3 && mappedConfig && loginProps.oauth?.profilCode !== ProfilCode.INVESTISSEUR) {
      mappedConfig = {...mappedConfig, description: 'Suite à de trop nombreux échecs dans le traitement de vos documents nous vous invitons à prendre contact avec le support Villyz à l\'adresse contact@villyz.fr afin de valider votre profil'}
    }

    return mappedConfig
  }, [status, kycErrorCount]);

  const showScaActionToDo = isScaModeActive && userInfo?.ProfilCode === ProfilCode.INVESTISSEUR && userInfo?.Etablissement === null && userInfo?.StatutScaCode !== StatutSca.VALIDE && userInfo.OnboardingStatus?.Code !== OnboardingStatus.ERREUR


  const registerTelAndValidateScaMessage = `Vous devez également remplir et valider votre numéro de téléphone dans la partie "Sécurité du compte".`
  const justValidateTelMessage = `Vous devez également valider votre numéro de téléphone dans la partie "Sécurité du compte".`

  const displayErrorHeaderMessage = () => {
    if(userInfo?.KYCValidated !== true && userInfo?.StatutScaCode === StatutSca.VALIDE){
      return <div style={{fontWeight: 'bold', color: '#a94442' }}>Pièce d’identité à valider</div>
    }
    else if(userInfo?.KYCValidated !== true && userInfo?.StatutScaCode !== StatutSca.VALIDE){
      return <div style={{fontWeight: 'bold', color: '#a94442' }}>Pièce d’identité et numéro de téléphone à vérifier</div>
    }
    else if(userInfo?.IsPiecesJustifDone !== false && userInfo?.StatutScaCode !== StatutSca.VALIDE){
      return <div style={{fontWeight: 'bold', color: '#a94442' }}>Numéro de téléphone à vérifier</div>
    }
  }



  return (
    <>
      <div
        key={status?.Code}
        css={{
          display: "block",
          background: activeConfig?.bgColor,
          color: activeConfig?.textColor,
          padding: "40px",
          fontSize: "30px",
          borderRadius: "0.28571429rem !important",
          marginBottom: "30px",
          boxShadow: "0px 1px 3px 0px #D4D4D5, 0px 0px 0px 1px #D4D4D5",
        }}
      >
        {activeConfig?.profile === Profil.INVESTISSEUR && (status?.Code === OnboardingStatus.COMPLET || status?.Code === OnboardingStatus.PRESQUE_COMPLET) ? 
          (<div css={{fontWeight: 'bold'}}>C’est parti!</div>) 
          : 
          (
            <>
              {status?.Code === OnboardingStatus.ERREUR && !isScaModeActive ? (
                <div css={{fontWeight: 'bold', color: '#a94442' }}>Pièce d’identité à valider</div>
              ) : 
              status?.Code === OnboardingStatus.ERREUR && isScaModeActive && userInfo?.Etablissement === null ? (
                displayErrorHeaderMessage()
              ) : (
                <>
                  Statut du profil :
                  <div
                    css={{
                      display: "inline-block",
                      fontWeight: "bold",
                      marginLeft: "0.5rem",
                    }}
                  >
                    {status?.Libelle}
                  </div>
                </>
              )}
            </>
          )}
        <div
          css={{
            display: "block",
            fontSize: "16px",
            marginTop: "10px",
          }}
        >
          {activeConfig?.description}
          {showScaActionToDo && 
          <p>
          {userInfo.IsScaDone ? 
            justValidateTelMessage
          :
            registerTelAndValidateScaMessage
          }
          </p>
          }
        </div>
      </div>
    </>
  );
}  